import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemSelector from './SystemSelector';
import { getHealthSystems } from '../../../utils/api';
import '../styles.css';

const SystemSelectionPage = () => {
  const [systemsList, setSystemsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch health systems from the API
  useEffect(() => {
    const loadSystems = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getHealthSystems(); // Use the API function
        // Ensure the response has the expected 'systems' property which is an array
        if (response && Array.isArray(response.systems)) {
          // Map API response to the structure expected by SystemSelector
          // API returns { system: 'name', patient_count: number }
          // SystemSelector expects { id: string|number, name: string, patientCount: number }
          const formattedSystems = response.systems.map((sys, index) => ({ 
            id: sys.system, // Use system name as ID for now, ensure it's unique
            name: sys.system,
            patientCount: sys.patient_count
          }));
          setSystemsList(formattedSystems);
        } else {
          console.error("API response format error:", response);
          setError('Received invalid data format from server.');
          setSystemsList([]); // Set to empty array on format error
        }
      } catch (err) {
        setError(err.message || 'Failed to load health systems. Please try again later.');
        console.error('Error loading health systems:', err);
        setSystemsList([]); // Ensure list is empty on error
      } finally {
        setLoading(false);
      }
    };

    loadSystems();
  }, []);

  // Handle system selection
  const handleSystemSelect = (system) => {
    // Navigate to the system detail page using the system name as the URL parameter
    // Convert system name to URL-friendly format
    // The 'id' in the system object passed here IS the system name from the API
    const systemNameForUrl = system.id.toLowerCase().replace(/\s+/g, '-');
    // Pass the selected system object (which includes name and patientCount) in state
    navigate(`/asthma_tracker/${systemNameForUrl}`, { state: { system } });
  };

  if (loading) {
    return <div className="loading-container">Loading Asthma Tracker data...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  return (
    <div className="asthma-tracker-container">
      <h1 className="page-title">Asthma Tracker</h1>
      
      {/* System Selector Section */}
      <div className="system-selector-section">
        <h2>Select Health System</h2>
        <p className="selection-instruction">
          Choose a health system to view detailed asthma metrics and analytics.
        </p>
        <SystemSelector 
          systems={systemsList} 
          selectedSystem={null}
          onSelectSystem={handleSystemSelect}
        />
      </div>
    </div>
  );
};

export default SystemSelectionPage; 